// File: src/components/admin/RecruitingPortal.jsx
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';

const SURVEY_PASS = 75;
const QUIZ_PASS = 85;
const QUIZ_MAX = 4;
const SURVEY_URL = 'https://jointribe.io/recruiting/survey';
const QUIZ_URL   = 'https://jointribe.io/recruiting/quiz';


const STAGES = [
  { id: 'survey_sent', label: 'Survey sent' },
  { id: 'survey_completed_pass', label: 'Survey completed (pass)' },
  { id: 'survey_completed_fail', label: 'Survey completed (fail)' },

  { id: 'interview_link_sent', label: 'Interview link sent' },
  { id: 'interview_scheduled', label: 'Interview scheduled' },
  { id: 'interview_declined', label: 'Interview declined' },
  { id: 'interview_no_show', label: 'Interview no-show' },
  { id: 'interview_failed', label: 'Interview failed' },
  { id: 'interview_passed', label: 'Interview passed' },

  { id: 'training_link_sent', label: 'Training link sent' },
  { id: 'training_confirmed', label: 'Training confirmed' },
  { id: 'training_declined', label: 'Training declined' },
  { id: 'training_no_show', label: 'Training no-show' },
  { id: 'training_failed', label: 'Training failed' },
  { id: 'training_passed', label: 'Training passed' },

  // Optional quiz stage set (only if you want stage control here)
  { id: 'quiz_sent', label: 'Quiz sent' },
  { id: 'quiz_submitted', label: 'Quiz submitted' },
  { id: 'quiz_failed', label: 'Quiz failed' },
  { id: 'quiz_passed', label: 'Quiz passed' },

  { id: 'onboarding_email_sent', label: 'Onboarding email sent' },
  { id: 'host_accepted', label: 'Host accepted' },
  { id: 'host_declined', label: 'Host declined' },
];


function pillStyle(kind) {
  const base = {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '4px 10px',
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 900,
    border: '1px solid #e5e7eb',
    whiteSpace: 'nowrap',
  };

  if (kind === 'good') return { ...base, background: '#ecfdf5', color: '#065f46' };
  if (kind === 'bad') return { ...base, background: '#fef2f2', color: '#991b1b' };
  if (kind === 'warn') return { ...base, background: '#fffbeb', color: '#92400e' };
  return { ...base, background: '#f8fafc', color: '#111' };
}

function trunc(s, n = 40) {
  const t = String(s || '').trim();
  if (!t) return '';
  return t.length > n ? t.slice(0, n) + '…' : t;
}

function pretty(obj) {
  try { return JSON.stringify(obj ?? {}, null, 2); } catch { return String(obj ?? ''); }
}

export default function RecruitingPortal() {
  const [loading, setLoading] = useState(true);
  const [apps, setApps] = useState([]);
  const [err, setErr] = useState('');

  const [query, setQuery] = useState('');
  const [stageFilter, setStageFilter] = useState('');

  const [selectedId, setSelectedId] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState(null);

  const [notes, setNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [saving, setSaving] = useState(false);

const [copiedMsg, setCopiedMsg] = useState('');

async function copyToClipboard(text, label) {
  try {
    await navigator.clipboard.writeText(text);
    setCopiedMsg(`${label} copied`);
    setTimeout(() => setCopiedMsg(''), 1200);
  } catch (e) {
    window.prompt('Copy this link:', text);
  }
}

  async function fetchList() {
    setLoading(true);
    setErr('');
    try {
      const qs = stageFilter ? `?stage=${encodeURIComponent(stageFilter)}` : '';
      const res = await axios.get(`/admin/recruiting/applications${qs}`);
      setApps(res.data?.applications || []);
    } catch (e) {
      setErr(e?.response?.data?.message || e?.message || 'Failed to load applicants');
    } finally {
      setLoading(false);
    }
  }

  async function openApplicant(id) {
    setSelectedId(id);
    setDetail(null);
    setDetailLoading(true);
    setErr('');
    try {
      const res = await axios.get(`/admin/recruiting/applications/${id}`);
      const app = res.data?.application || null;
      setDetail(app);
      setNotes(String(app?.notes || ''));
      setRejectionReason(String(app?.rejectionReason || ''));
    } catch (e) {
      setErr(e?.response?.data?.message || e?.message || 'Failed to load applicant');
    } finally {
      setDetailLoading(false);
    }
  }

  function closeApplicant() {
    setSelectedId(null);
    setDetail(null);
    setNotes('');
    setRejectionReason('');
  }

  async function patchApplicant(id, patch) {
    setSaving(true);
    setErr('');
    try {
      const res = await axios.patch(`/admin/recruiting/applications/${id}`, patch);
      const updated = res.data?.application;

      setApps(prev => prev.map(a => (String(a._id) === String(id) ? { ...a, ...updated } : a)));
      setDetail(updated);

      if (patch.notes !== undefined) setNotes(String(patch.notes || ''));
      if (patch.rejectionReason !== undefined) setRejectionReason(String(patch.rejectionReason || ''));
    } catch (e) {
      setErr(e?.response?.data?.message || e?.message || 'Failed to update applicant');
    } finally {
      setSaving(false);
    }
  }

    async function deleteApplicant(id) {
    const ok = window.confirm('Delete this submission? This cannot be undone.');
    if (!ok) return;

    setSaving(true);
    setErr('');
    try {
      await axios.delete(`/admin/recruiting/applications/${id}`);

      setApps(prev => prev.filter(a => String(a._id) !== String(id)));

      if (String(selectedId) === String(id)) {
        closeApplicant();
      }
    } catch (e) {
      setErr(e?.response?.data?.message || e?.message || 'Failed to delete applicant');
    } finally {
      setSaving(false);
    }
  }

  useEffect(() => { fetchList(); }, [stageFilter]);

  const rows = useMemo(() => {
    const q = String(query || '').trim().toLowerCase();
    if (!q) return apps;

    return (apps || []).filter(a => {
      const name = String(a?.name || '').toLowerCase();
      const email = String(a?.email || '').toLowerCase();
      return name.includes(q) || email.includes(q);
    });
  }, [apps, query]);

  function surveyPass(a) {
    const score = Number(a?.survey?.score ?? NaN);
    const hardFail = !!a?.survey?.answers?.hardFail;
    if (!Number.isFinite(score)) return { status: '—', kind: 'neutral' };
    if (hardFail) return { status: 'HARD FAIL', kind: 'bad' };
    if (score >= SURVEY_PASS) return { status: `PASS (${score})`, kind: 'good' };
    return { status: `FAIL (${score})`, kind: 'bad' };
  }

  function quizStatus(a) {
    const attempts = Number(a?.quiz?.attempts || 0);
    const score = a?.quiz?.score;
    const passed = a?.quiz?.passed;

    if (score == null && passed == null) return { status: `— (0/${QUIZ_MAX})`, kind: 'neutral' };

    const scoreNum = Number(score);
    const passByScore = Number.isFinite(scoreNum) && scoreNum >= QUIZ_PASS;
    const isPass = passed === true || passByScore;

    if (isPass) return { status: `PASS (${scoreNum}) (${attempts}/${QUIZ_MAX})`, kind: 'good' };

    if (attempts >= QUIZ_MAX) return { status: `FAIL (${scoreNum}) (${attempts}/${QUIZ_MAX})`, kind: 'bad' };

    return { status: `IN PROGRESS (${scoreNum}) (${attempts}/${QUIZ_MAX})`, kind: 'warn' };
  }

  return (
    <div style={{ marginTop: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-end' }}>
        <div>
          <div style={{ fontWeight: 900, fontSize: 18, color: '#111' }}>Recruiting</div>
          <div style={{ fontSize: 12, color: '#6b7280' }}>
            {loading ? 'Loading…' : `${rows.length} candidates`}
          </div>
        </div>

  <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
  {/* Links (left of search bar) */}
  <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
    {/* Survey */}
    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
      <input
        value={SURVEY_URL}
        readOnly
        style={{
          height: 36,
          width: 280,
          borderRadius: 10,
          border: '1px solid #e5e7eb',
          padding: '0 10px',
          fontSize: 12,
          fontWeight: 800,
          color: '#111',
          background: '#fff',
        }}
      />
      <button
        type="button"
        onClick={() => copyToClipboard(SURVEY_URL, 'Survey URL')}
        style={{
          height: 36,
          padding: '0 10px',
          borderRadius: 10,
          border: '1px solid #e5e7eb',
          background: '#fff',
          fontWeight: 900,
          cursor: 'pointer',
          whiteSpace: 'nowrap',
        }}
      >
        Copy
      </button>
    </div>

    {/* Quiz */}
    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
      <input
        value={QUIZ_URL}
        readOnly
        style={{
          height: 36,
          width: 260,
          borderRadius: 10,
          border: '1px solid #e5e7eb',
          padding: '0 10px',
          fontSize: 12,
          fontWeight: 800,
          color: '#111',
          background: '#fff',
        }}
      />
      <button
        type="button"
        onClick={() => copyToClipboard(QUIZ_URL, 'Quiz URL')}
        style={{
          height: 36,
          padding: '0 10px',
          borderRadius: 10,
          border: '1px solid #e5e7eb',
          background: '#fff',
          fontWeight: 900,
          cursor: 'pointer',
          whiteSpace: 'nowrap',
        }}
      >
        Copy
      </button>
    </div>
  </div>

  <input
    value={query}
    onChange={(e) => setQuery(e.target.value)}
    placeholder="Search name or email…"
    style={{
      height: 36,
      width: 220,
      borderRadius: 10,
      border: '1px solid #e5e7eb',
      padding: '0 10px'
    }}
  />

  <select
    value={stageFilter}
    onChange={(e) => setStageFilter(e.target.value)}
    style={{ height: 36, borderRadius: 10, border: '1px solid #e5e7eb', padding: '0 10px' }}
  >
    <option value="">All stages</option>
    {STAGES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
  </select>

  <button
    onClick={fetchList}
    style={{ height: 36, padding: '0 12px', borderRadius: 10, border: '1px solid #e5e7eb', background: '#111', color: '#fff', fontWeight: 900 }}
  >
    Refresh
  </button>
</div>

{copiedMsg ? (
  <div style={{ marginTop: 8, fontSize: 12, color: '#065f46', fontWeight: 900 }}>
    {copiedMsg}
  </div>
) : null}


      </div>

      {err ? (
        <div style={{ marginTop: 12, padding: 10, borderRadius: 12, border: '1px solid #fecaca', background: '#fef2f2', color: '#991b1b', fontWeight: 800 }}>
          {err}
        </div>
      ) : null}

      <div style={{ marginTop: 12, border: '1px solid #e5e7eb', borderRadius: 14, overflow: 'hidden', background: '#fff' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
  <tr style={{ background: '#f9fafb' }}>
    <th style={{ padding: 10, textAlign: 'left' }}>Candidate</th>
    <th style={{ padding: 10, textAlign: 'left' }}>Stage</th>
    <th style={{ padding: 10, textAlign: 'left' }}>Survey</th>
    <th style={{ padding: 10, textAlign: 'left' }}>Quiz</th>
    <th style={{ padding: 10, textAlign: 'left' }}>Availability</th>
    <th style={{ padding: 10, textAlign: 'left' }}>Notes</th>
    <th style={{ padding: 10, textAlign: 'left' }}>Updated</th>
    <th style={{ padding: 10, textAlign: 'left' }}>Actions</th>
  </tr>
</thead>

          <tbody>
            {loading ? (
<tr><td colSpan={8} style={{ padding: 12, color: '#6b7280' }}>Loading…</td></tr>
            ) : rows.length === 0 ? (
<tr><td colSpan={8} style={{ padding: 12, color: '#6b7280' }}>No candidates yet.</td></tr>
            ) : rows.map(a => {
              const s = surveyPass(a);
              const q = quizStatus(a);
              const avail = a?.survey?.answers
                ? `${a.survey.answers.daysAvailable || '—'} / ${(a.survey.answers.timesAvailable || []).join(', ') || '—'} / ${a.survey.answers.hostFreq || '—'}`
                : '—';

              return (
                <tr key={a._id} style={{ borderTop: '1px solid #eee', cursor: 'pointer' }} onClick={() => openApplicant(a._id)}>
                  <td style={{ padding: 10 }}>
                    <div style={{ fontWeight: 900, color: '#111' }}>{a.name || a.email}</div>
                    <div style={{ fontSize: 12, color: '#6b7280' }}>{a.email}</div>
                  </td>

                  <td style={{ padding: 10 }}>
                    <span style={pillStyle('neutral')}>{a.stage || '—'}</span>
                  </td>

                  <td style={{ padding: 10 }}>
                    <span style={pillStyle(s.kind)}>{s.status}</span>
                  </td>

                  <td style={{ padding: 10 }}>
                    <span style={pillStyle(q.kind)}>{q.status}</span>
                  </td>

                  <td style={{ padding: 10, fontSize: 12, color: '#111' }}>
                    {avail}
                  </td>

                  <td style={{ padding: 10, fontSize: 12, color: '#111' }}>
                    {trunc(a.notes)}
                  </td>

                  <td style={{ padding: 10, fontSize: 12, color: '#6b7280' }}>
                    {a.updatedAt ? new Date(a.updatedAt).toLocaleString() : '—'}
                  </td>
                  <td style={{ padding: 10 }}>
  <button
    type="button"
    disabled={saving}
    onClick={(e) => {
      e.stopPropagation();
      deleteApplicant(a._id);
    }}
    style={{
      height: 32,
      padding: '0 10px',
      borderRadius: 10,
      border: '1px solid #fecaca',
      background: '#fff',
      color: '#991b1b',
      fontWeight: 900,
      cursor: 'pointer',
      whiteSpace: 'nowrap',
    }}
  >
    Delete
  </button>
</td>

                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Drawer */}
      {selectedId ? (
        <div
          onClick={closeApplicant}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 9999, display: 'flex', justifyContent: 'flex-end' }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ width: 560, maxWidth: '92vw', height: '100%', background: '#fff', borderLeft: '1px solid #e5e7eb', padding: 14, overflow: 'auto' }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
              <div>
                <div style={{ fontWeight: 900, fontSize: 16, color: '#111' }}>{detail?.name || detail?.email || 'Candidate'}</div>
                <div style={{ fontSize: 12, color: '#6b7280' }}>{detail?.email || ''}</div>
                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                  Stage: <b style={{ color: '#111' }}>{detail?.stage || '—'}</b>
                </div>
              </div>
              <button onClick={closeApplicant} style={{ height: 34, padding: '0 10px', borderRadius: 10, border: '1px solid #e5e7eb', background: '#fff', fontWeight: 800 }}>
                Close
              </button>
            </div>

            {detailLoading ? (
              <div style={{ marginTop: 14, color: '#6b7280' }}>Loading…</div>
            ) : !detail ? (
              <div style={{ marginTop: 14, color: '#6b7280' }}>No data.</div>
            ) : (
              <>
                <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid #e5e7eb' }}>
                  <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 8 }}>Stage</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {STAGES.map(s => {
                      const active = s.id === detail.stage;
                      return (
                        <button
                          key={s.id}
                          disabled={saving}
                          onClick={() => patchApplicant(detail._id, { stage: s.id })}
                          style={{
                            height: 34,
                            padding: '0 10px',
                            borderRadius: 999,
                            border: '1px solid #e5e7eb',
                            background: active ? '#111' : '#fff',
                            color: active ? '#fff' : '#111',
                            fontWeight: 900,
                          }}
                        >
                          {s.label}
                        </button>
                      );
                    })}
                  </div>
                  {saving ? <div style={{ marginTop: 8, fontSize: 12, color: '#6b7280' }}>Saving…</div> : null}
                </div>

                <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid #e5e7eb' }}>
                  <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 8 }}>Notes</div>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Write quick notes…"
                    style={{ width: '100%', minHeight: 90, borderRadius: 12, border: '1px solid #e5e7eb', padding: 10 }}
                  />
                  <div style={{ marginTop: 8 }}>
                    <button
                      disabled={saving}
                      onClick={() => patchApplicant(detail._id, { notes })}
                      style={{ height: 36, padding: '0 12px', borderRadius: 10, border: '1px solid #e5e7eb', background: '#111', color: '#fff', fontWeight: 900 }}
                    >
                      Save notes
                    </button>
                  </div>
                </div>

                <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid #e5e7eb' }}>
                  <div style={{ fontSize: 12, fontWeight: 900, marginBottom: 8 }}>Rejection reason</div>
                  <input
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    placeholder="e.g. not a fit, low quiz score…"
                    style={{ width: '100%', height: 38, borderRadius: 12, border: '1px solid #e5e7eb', padding: '0 10px' }}
                  />
                  <div style={{ marginTop: 8 }}>
                    <button
                      disabled={saving}
                      onClick={() => patchApplicant(detail._id, { rejectionReason })}
                      style={{ height: 36, padding: '0 12px', borderRadius: 10, border: '1px solid #e5e7eb', background: '#fff', fontWeight: 900 }}
                    >
                      Save reason
                    </button>
                  </div>
                </div>

                <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid #e5e7eb' }}>
                  <div style={{ fontSize: 14, fontWeight: 900, color: '#111' }}>Survey</div>
                  <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                    Score: <b style={{ color: '#111' }}>{detail?.survey?.score ?? '—'}</b>
                  </div>
                  <pre style={{ marginTop: 10, background: '#0b0b0b', color: '#f9fafb', padding: 12, borderRadius: 12, fontSize: 12, overflow: 'auto' }}>
                    {pretty(detail?.survey?.answers)}
                  </pre>
                </div>

                <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid #e5e7eb' }}>
                  <div style={{ fontSize: 14, fontWeight: 900, color: '#111' }}>Quiz</div>
                  <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                    Score: <b style={{ color: '#111' }}>{detail?.quiz?.score ?? '—'}</b> • Passed:{' '}
                    <b style={{ color: '#111' }}>{detail?.quiz?.passed == null ? '—' : detail.quiz.passed ? 'Yes' : 'No'}</b> • Attempts:{' '}
                    <b style={{ color: '#111' }}>{detail?.quiz?.attempts ?? 0}/{QUIZ_MAX}</b>
                  </div>
                  <pre style={{ marginTop: 10, background: '#0b0b0b', color: '#f9fafb', padding: 12, borderRadius: 12, fontSize: 12, overflow: 'auto' }}>
                    {pretty(detail?.quiz?.answers)}
                  </pre>
                </div>
              </>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
